import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  ControlValueAccessor,
  FormBuilder,
  FormControl,
  FormGroup,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CUSTOM_INSTRUCTION_ERROR_MESSAGES } from '@constants/custom-instructions.constants';
import { ConfigService } from '@services/config.service';
import {
  CreateCustomInstructionRequest,
  CustomInstruction,
  CustomInstructionsService,
} from '@services/custom-instructions.service';
import { MessageService } from 'primeng/api';
import {
  AutoComplete,
  AutoCompleteCompleteEvent,
  AutoCompleteSelectEvent,
} from 'primeng/autocomplete';
import { ButtonModule } from 'primeng/button';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { IftaLabelModule } from 'primeng/iftalabel';
import { TextareaModule } from 'primeng/textarea';
import { of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';

import {
  SaveAsDialogComponent,
  SaveAsDialogData,
} from './save-as-dialog/save-as-dialog.component';

export interface CustomInstructionFormState {
  name: FormControl<string>;
  content: FormControl<string>;
}

export interface CustomInstructionValue {
  name: string;
  content: string;
}

@Component({
  selector: 'app-custom-instructions-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    IftaLabelModule,
    AutoComplete,
    TextareaModule,
    ButtonModule,
  ],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomInstructionsFormComponent),
      multi: true,
    },
    DialogService,
  ],
  templateUrl: './custom-instructions-form.component.html',
  styleUrls: ['./custom-instructions-form.component.css'],
})
export class CustomInstructionsFormComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @Input() disabled = false;

  form: FormGroup<CustomInstructionFormState>;

  customInstructions: CustomInstruction[] = [];
  customInstructionOptions: CustomInstruction[] = [];
  selectedInstruction: CustomInstruction | undefined = undefined;

  isLoading = signal(false);
  isSaving = signal(false);
  error = signal('');

  private saveAsDialogRef: DynamicDialogRef | undefined;

  constructor(
    private formBuilder: FormBuilder,
    private customInstructionsService: CustomInstructionsService,
    private destroyRef: DestroyRef,
    private dialogService: DialogService,
    private configService: ConfigService,
    private messageService: MessageService,
  ) {}

  writeValue(value: CustomInstructionValue | null): void {
    if (!value) {
      return;
    }

    const selectedInstruction = this.customInstructions.find(
      instruction => instruction.name === value.name,
    );

    if (selectedInstruction) {
      this.selectedInstruction = selectedInstruction;
    }

    this.form.setValue(value);
  }

  ngOnInit() {
    this.createForm();
    this.loadCustomInstructions();
  }

  ngOnDestroy() {
    if (this.saveAsDialogRef) {
      this.saveAsDialogRef.close();
    }
  }

  private createForm() {
    const inputLengths = this.configService.inputLengths;

    this.form = this.formBuilder.group<CustomInstructionFormState>({
      name: this.formBuilder.control(''),
      content: this.formBuilder.control(
        '',
        Validators.maxLength(inputLengths.CUSTOM_INSTRUCTION_CONTENT),
      ),
    });
  }

  registerOnChange(fn: any): void {
    this.form.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(fn);
  }

  registerOnTouched(fn: any): void {
    /** noop */
  }

  private loadCustomInstructions() {
    // Only show loading if we don't have any data yet
    const hasData = this.customInstructions.length > 0;
    if (!hasData) {
      this.isLoading.set(true);
    }

    this.customInstructionsService
      .getCustomInstructions()
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        catchError(error => {
          console.error('Error loading custom instructions:', error);
          this.error.set(CUSTOM_INSTRUCTION_ERROR_MESSAGES.LOAD_FAILED);
          this.isLoading.set(false);
          return of([]); // Return empty array on error
        }),
      )
      .subscribe(customInstructions => {
        if (!customInstructions) {
          return;
        }
        this.customInstructionOptions = this.customInstructions =
          customInstructions;
        this.isLoading.set(false); // Reset loading after each successful update
      });
  }

  private selectCustomInstruction(instruction: CustomInstruction) {
    this.selectedInstruction = instruction;

    this.form.patchValue({
      name: instruction.name,
      content: instruction.content,
    });
  }

  filterCustomInstructions({ query }: AutoCompleteCompleteEvent) {
    const customInstructionOptions = this.customInstructions.filter(
      instruction => {
        return instruction.name
          .toLocaleLowerCase()
          .includes(query.toLocaleLowerCase());
      },
    );

    this.customInstructionOptions = customInstructionOptions;
  }

  onSelect({ value }: AutoCompleteSelectEvent) {
    this.selectCustomInstruction(value);
  }

  onSaveClick() {
    const [name, content] = [
      this.form.get('name').value.trim(),
      this.form.get('content').value.trim(),
    ];

    if (!name || !content) {
      return;
    }

    this.isSaving.set(true);
    this.error.set('');

    if (this.selectedInstruction) {
      const updateData: CreateCustomInstructionRequest = {
        name,
        content,
      };

      this.customInstructionsService
        .updateCustomInstruction(this.selectedInstruction._id, updateData)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(error => {
            console.error('Error saving custom instruction:', error);
            this.error.set(CUSTOM_INSTRUCTION_ERROR_MESSAGES.SAVE_FAILED);
            return of(null);
          }),
          finalize(() => {
            this.isSaving.set(false);
          }),
        )
        .subscribe(updatedInstruction => {
          if (!updatedInstruction) return;

          this.selectCustomInstruction(updatedInstruction);

          this.messageService.add({
            severity: 'success',
            summary: 'Saved',
            detail: `"${updatedInstruction.name}" has been updated successfully.`,
            life: 3000,
          });
        });
    } else {
      const createData: CreateCustomInstructionRequest = {
        name,
        content,
      };

      this.customInstructionsService
        .createCustomInstruction(createData)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(error => {
            console.error('Error creating custom instruction:', error);
            this.error.set(CUSTOM_INSTRUCTION_ERROR_MESSAGES.SAVE_FAILED);
            return of(null);
          }),
          finalize(() => {
            this.isSaving.set(false);
          }),
        )
        .subscribe(newInstruction => {
          if (!newInstruction) return;

          this.selectCustomInstruction(newInstruction);

          this.messageService.add({
            severity: 'success',
            summary: 'Created',
            detail: `"${newInstruction.name}" has been created successfully.`,
            life: 3000,
          });
        });
    }
  }

  onSaveAsClick() {
    // Additional safety check - should not reach here if no instructions exist
    if (this.customInstructions.length === 0) {
      console.warn('Save As clicked but no custom instructions available');
      return;
    }

    const [name, content] = [
      this.form.get('name').value.trim(),
      this.form.get('content').value.trim(),
    ];

    if (!name || !content) {
      return;
    }

    if (!this.selectedInstruction) {
      console.warn(
        'Save As clicked but no parent custom instructions selected',
      );
      return;
    }

    const parentId = this.selectedInstruction._id;
    const suggestedName = `${this.selectedInstruction.name} (Copy)`;

    const dialogData: SaveAsDialogData = {
      parentId: parentId,
      content: content,
      suggestedName: suggestedName,
    };

    this.saveAsDialogRef = this.dialogService.open(SaveAsDialogComponent, {
      header: 'Save As...',
      width: '450px',
      modal: true,
      closable: true,
      data: dialogData,
    });

    this.saveAsDialogRef.onClose
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((newInstruction: CustomInstruction | undefined) => {
        if (!newInstruction) return;

        this.selectCustomInstruction(newInstruction);

        this.messageService.add({
          severity: 'success',
          summary: 'Saved As',
          detail: `"${newInstruction.name}" has been created successfully.`,
          life: 3000,
        });
      });
  }

  get contentLength(): number {
    return this.form.controls.content.value?.length || 0;
  }

  get maxContentLength(): number {
    return this.configService.inputLengths.CUSTOM_INSTRUCTION_CONTENT;
  }

  get isContentTooLong(): boolean {
    return this.contentLength > this.maxContentLength;
  }

  get contentValidationMessage(): string {
    if (this.isContentTooLong) {
      return `Content is too long (${this.contentLength}/${this.maxContentLength} characters)`;
    }
    return '';
  }
}
